import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Phone, Sparkles } from "lucide-react";
import { GemMark } from "./Logo";
import { scrollToId } from "./Reveal";
import { CONTACT } from "@/lib/content";

const EASE = [0.16, 1, 0.3, 1];
const LINES = [
  { text: "Clean,", cls: "text-white" },
  { text: "cut to", cls: "text-outline" },
  { text: "brilliance.", cls: "text-amber-300" },
];

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%", rotate: 3 }} animate={{ y: "0%", rotate: 0 }} transition={{ duration: 1.1, delay: 0.15 + i * 0.13, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const RingText = () => (
  <svg viewBox="0 0 200 200" className="spin-slow h-full w-full">
    <defs>
      <path id="ring" d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0" />
    </defs>
    <text className="font-mono-x" fill="#FBBF24" fontSize="13" letterSpacing="5">
      <textPath href="#ring">SPOTLESS • VETTED • INSURED • CITRINE • </textPath>
    </text>
  </svg>
);

const TiltFrame = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <div className="relative mx-auto w-full max-w-[460px] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-tilt-frame">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative aspect-[4/5]">
        <div className="absolute inset-0 rounded-[2rem] bg-amber-400/20 blur-3xl" />
        <div className="gem-clip absolute inset-0 overflow-hidden bg-slate-800">
          <motion.img src="/img/hero.jpg" alt="Spotless bright kitchen" className="h-full w-full scale-110 object-cover" initial={{ scale: 1.35, filter: "brightness(0.3)" }} animate={{ scale: 1.1, filter: "brightness(0.95)" }} transition={{ duration: 1.8, ease: EASE }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/70 via-transparent to-transparent" />
        </div>
        <div style={{ transform: "translateZ(80px)" }} className="absolute -left-6 bottom-[14%] flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0B0F17]/80 px-4 py-3 backdrop-blur-xl">
          <Sparkles className="h-5 w-5 text-amber-300" />
          <div>
            <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-slate-400">Every visit</p>
            <p className="text-sm font-semibold text-white">Checked room by room</p>
          </div>
        </div>
        <div style={{ transform: "translateZ(120px)" }} className="absolute -right-4 -top-4 h-28 w-28 sm:h-32 sm:w-32">
          <RingText />
          <GemMark className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2" />
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = ({ onTab }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute right-0 top-1/3 h-[380px] w-[380px] rounded-full bg-emerald-400/5 blur-[120px]" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-14 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-28 lg:pt-20">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-8 inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/5 px-4 py-1.5 font-mono-x text-xs uppercase tracking-[0.2em] text-amber-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Home & commercial cleaning
          </motion.p>
          <h1 className="font-display text-[2.9rem] font-extrabold leading-[0.95] tracking-tight sm:text-7xl lg:text-[5.6rem] xl:text-[6.2rem]" data-testid="hero-title">
            {LINES.map((l, i) => (
              <MaskLine key={l.text} i={i} className={l.cls}>
                {l.text}
              </MaskLine>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: EASE }} className="mt-8 max-w-lg text-base leading-relaxed text-slate-300 md:text-lg">
            Citrine Home Solutions brings detail-obsessed cleaning to homes, offices, rentals and fresh builds. Pick your services, choose a slot, and come home to a place that sparkles.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={() => { onTab?.("book"); scrollToId("book"); }} data-testid="hero-book-btn" className="group inline-flex items-center gap-3 rounded-full bg-amber-400 py-3 pl-6 pr-3 font-semibold text-[#0B0F17] transition-colors hover:bg-amber-300">
              Book a cleaning
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0B0F17] text-amber-300 transition-transform duration-300 group-hover:rotate-[-45deg]">
                <ArrowDownRight className="h-4 w-4" />
              </span>
            </motion.button>
            <button onClick={() => { onTab?.("quote"); scrollToId("quote"); }} data-testid="hero-quote-btn" className="rounded-full border border-white/20 px-6 py-4 font-semibold text-white transition-colors hover:border-amber-300 hover:text-amber-300">
              Free quote
            </button>
            <a href={CONTACT.phoneHref} data-testid="hero-call-link" className="inline-flex items-center gap-2 px-2 text-sm text-slate-300 transition-colors hover:text-amber-300">
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yImg }} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.2, ease: EASE }} className="px-4 lg:col-span-5 lg:px-0">
          <TiltFrame />
        </motion.div>
      </div>
    </section>
  );
};
